import { useInfiniteQuery } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { ProtectedPersonCard } from '../components/executive-protection/ProtectedPersonCard';
import { executiveProtectionService } from '../services/executive-protection.service';
import { Button } from '../components/ui/Button';

const PERSONS_PER_PAGE = 10;

export default function ProtectedPersons() {
  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
  } = useInfiniteQuery({
    queryKey: ['executive-protection', 'persons', 'paginated'],
    queryFn: ({ pageParam = 1 }) =>
      executiveProtectionService.getProtectedPersons({ page: pageParam, limit: PERSONS_PER_PAGE }),
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      const { page, totalPages } = lastPage.pagination;
      return page < totalPages ? page + 1 : undefined;
    },
    refetchInterval: 300000,
    staleTime: 240000,
  });

  const allPersons = data?.pages.flatMap((page) => page.protectedPersons) ?? [];
  const total = data?.pages[0]?.pagination.total ?? 0;
  const remaining = total - allPersons.length;

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.4, ease: 'easeOut' },
    },
  };

  return (
    <div>
      <div className="mb-6">
        <h2 className="text-xl font-semibold text-secondary-900 dark:text-white">
          Protected Persons
        </h2>
      </div>
      {isLoading ? (
        <div className="text-center py-8 text-secondary-500">Loading protected persons...</div>
      ) : allPersons.length === 0 ? (
        <div className="text-center py-8 text-secondary-500">No protected persons found</div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {allPersons.map((person) => (
              <motion.div
                key={person.id}
                variants={itemVariants}
                layout
                className="h-full"
              >
                <ProtectedPersonCard person={person} />
              </motion.div>
            ))}
          </div>
          {hasNextPage && (
            <div className="flex justify-center mt-6">
              <Button
                variant="outline"
                onClick={() => fetchNextPage()}
                loading={isFetchingNextPage}
                disabled={isFetchingNextPage}
              >
                {isFetchingNextPage ? 'Loading...' : `Load More (${remaining} remaining)`}
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
